<template>
  <div>
    <div v-if="isValueType">
      <el-input
        :autosize="{ minRows: 1, maxRows: 10 }"
        :placeholder="valuePlaceholderText"
        :value="valueModel"
        readonly
        type="textarea"
      />
    </div>

    <el-input
      v-if="isLinkType"
      :autosize="{ minRows: 1, maxRows: 4 }"
      :placeholder="linkPlaceholderText"
      :value="linkModel"
      readonly
      type="textarea"
    />


    <div v-if="isCssType">
      <div class="block">
        <span>Font Size ({{ css['font-size'] }}px)</span>
        <el-slider
          v-model="css['font-size']"
          :max="80"
          :step="1"
          readonly
        />
      </div>

      <div class="block">
        <span>Font Weight ({{ css['font-weight'] }})</span>
        <el-slider
          v-model="css['font-weight']"
          :format-tooltip="formatFontWeightTooltip"
          :min="100"
          :max="1000"
          :step="100"
          readonly
        />
      </div>

      <div class="block">
        <span>Style</span>
        <el-select
          v-model="css['text-decoration']"
          readonly
          placeholder="Select"
          size="mini"
        >
          <el-option
            v-for="item in textDecorationList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
    </div>

    <div
      v-if="isProductType"
      class="product-input-container"
    >
      <div
        v-for="(product, i) in products"
        :key="i"
        class="product-input-item"
      >
        <span>#{{ i + 1 }}</span>
        <el-input
          v-model.number="product.product_id"
          readonly
          placeholder="ID"
          clearable
          class="product-id-input"/>
        <el-input
          v-model.trim="product.url"
          readonly
          placeholder="Link"
          clearable
          class="product-link-input"/>
      </div>
    </div>

    <span v-if="isUnknownType">Unknown Type</span>
  </div>
</template>

<script>
import { EDITORIAL_ITEMS } from '../../../utils/const'
import { stripTags } from '../../../utils/helper'

export default {
  name: 'EditorialMainItem',
  props: {
    updatedInputType: {
      type: [String],
      default: 'value',
    },
    itemType: {
      type: String,
      required: true,
      validator(value) {
        return EDITORIAL_ITEMS.find(e => e.value === value)
      },
    },
    link: {
      type: [String],
      default: '',
    },
    css: {
      type: [Object],
      default: undefined,
    },
    value: {
      type: [String],
      default: '',
    },
    products: {
      type: Array,
      default: undefined,
    },
  },
  data() {
    return {
      textDecorationList: [
        { value: 'none', label: 'None' },
        { value: 'underline', label: 'Underline' },
      ],
    }
  },
  computed: {
    isValueType() {
      const inputType = 'value'
      return this.checkHasItemInputType(this.itemType, inputType)
    },
    isLinkType() {
      const inputType = 'link'
      return this.checkHasItemInputType(this.itemType, inputType)
    },
    isCssType() {
      const inputType = 'css'
      return this.checkHasItemInputType(this.itemType, inputType)
    },
    isProductType() {
      const inputType = 'product'
      return this.checkHasItemInputType(this.itemType, inputType)
    },
    isNotProductType() {
      const inputType = 'product'
      return !this.checkHasItemInputType(this.itemType, inputType)
    },
    isUnknownType() {
      return !this.isValueType && !this.isProductType && !this.isLinkType && !this.isCssType
    },
    valuePlaceholderText() {
      switch (this.itemType) {
        case 'text':
          return 'Text'
        case 'text_link':
          return 'Title'
        case 'image':
        case 'image_link':
          return 'Image link'
        case 'youtube':
        case 'instagram':
          return 'Embed code'
        default:
          return 'Value'
      }
    },
    linkPlaceholderText() {
      switch (this.itemType) {
        case 'text_link':
          return 'Outlink (leave blank if none)'
        default:
          return 'Outlink'
      }
    },
    valueModel() {
      return this.transformValue(this.itemType, this.value)
    },
    linkModel: {
      get() {
        return this.link
      },
    },
  },
  methods: {
    transformValue(type, value) {
      const map = new Map([
        ['text', this.getTextValue],
        ['instagram', this.getInstagramLink],
        ['youtube', this.getYoutubeLink],
      ])
      const handler = map.get(type)
      if (handler) {
        return handler(value)
      }
      return value
    },
    getTextValue(str) {
      let result = stripTags(str)

      const anchorMatches = str.matchAll(/<a href="(.*?)">(.*?)<\/a>/g)
      const matchesAnchorItems = Array.from(anchorMatches, ([, link = '', text = '']) => ({
        link, text,
      }))
      if (matchesAnchorItems.length > 0) {
        result += '\n\n----- Note -----\n\n' // separator
        result += matchesAnchorItems
          .map(({ text, link }) => `Text: ${text}\nLink: ${link}\n`)
          .join('\n')
      }
      return result
    },
    getInstagramLink(str) {
      const result = /data-instgrm-permalink="(.*?)"/.exec(str)
      if (result && result[1]) {
        return result[1].split('/?')[0]
      }
      return ''
    },
    getYoutubeLink(str) {
      return `https://www.youtube.com/watch?v=${str}`
    },
    updateItem(inputType) {
      this.$emit(inputType)
    },
    formatFontWeightTooltip(value) {
      if (value === 700) {
        return `${value} Bold`
      }
      if (value === 500) {
        return `${value} Normal`
      }
      return value
    },
    checkHasItemInputType(itemType, inputType) {
      for (let i = 0; i < EDITORIAL_ITEMS.length; i += 1) {
        const item = EDITORIAL_ITEMS[i]
        if (item.value === itemType && item.types.indexOf(inputType) !== -1) {
          return true
        }
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
.product-input-container {
  .product-input-item {
    display: flex;

    &:not(:last-child) {
      margin-bottom: 8px;
    }
    span {
      width: 20px;
      margin: auto 10px auto auto;
    }
    .product-id-input {
      margin-right: 10px;
      max-width: 130px;
    }
  }
}

.block {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-content: center;
  span {
    margin: auto 0;
  }
}

.el-textarea + .el-textarea {
  margin-top: 8px;
}
.el-slider {
  width: 50%;
}
</style>

